import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid2 as Grid,
  Switch,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Build as BuildIcon,
  Computer as ComputerIcon,
  AdminPanelSettings as AdminIcon,
  PhoneAndroid as MobileIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface MaintenanceApp {
  id: number;
  app: string;
  ativo: boolean;
  titulo: string;
  mensagem: string;
  data_inicio: string | null;
  data_fim_prevista: string | null;
  updated_at: string;
}

const APP_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  frontend: { label: 'Frontend (Clientes)', icon: <ComputerIcon />, color: '#6233A0' },
  admin: { label: 'Admin (Backoffice)', icon: <AdminIcon />, color: '#1976d2' },
  mobile: { label: 'App Mobile', icon: <MobileIcon />, color: '#2e7d32' },
};

const MaintenanceCard: React.FC<{
  app: MaintenanceApp;
  onSave: (app: string, data: Partial<MaintenanceApp>) => Promise<void>;
}> = ({ app, onSave }) => {
  const [form, setForm] = useState({
    ativo: app.ativo,
    titulo: app.titulo || 'Em Manutencao',
    mensagem: app.mensagem || '',
    data_fim_prevista: app.data_fim_prevista ? app.data_fim_prevista.slice(0, 16) : '',
  });
  const [saving, setSaving] = useState(false);

  const config = APP_CONFIG[app.app] || { label: app.app, icon: <BuildIcon />, color: '#666' };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(app.app, {
        ativo: form.ativo,
        titulo: form.titulo,
        mensagem: form.mensagem,
        data_inicio: form.ativo ? new Date().toISOString() : null,
        data_fim_prevista: form.data_fim_prevista || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ border: form.ativo ? '2px solid #f44336' : '1px solid #e0e0e0' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ color: config.color }}>{config.icon}</Box>
            <Typography variant="h6">{config.label}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              size="small"
              label={form.ativo ? 'Em Manutencao' : 'Online'}
              color={form.ativo ? 'error' : 'success'}
            />
            <Switch
              checked={form.ativo}
              onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
              color="error"
            />
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              label="Titulo"
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={3}
              label="Mensagem para os utilizadores"
              value={form.mensagem}
              onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Previsao de retorno"
              value={form.data_fim_prevista}
              onChange={(e) => setForm({ ...form, data_fim_prevista: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              color={form.ativo ? 'error' : 'primary'}
            >
              {saving ? 'A guardar...' : 'Guardar'}
            </Button>
          </Grid>
        </Grid>

        {app.updated_at && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Ultima atualizacao: {new Date(app.updated_at).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const Maintenance: React.FC = () => {
  const [apps, setApps] = useState<MaintenanceApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const { data } = await api.get('/admin/settings/maintenance');
      if (data.success) {
        setApps(data.data);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSave = async (app: string, data: Partial<MaintenanceApp>) => {
    try {
      setError(null);
      setSuccess(null);
      await api.put(`/admin/settings/maintenance/${app}`, data);
      setSuccess(`${APP_CONFIG[app]?.label || app} atualizado com sucesso`);
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <BuildIcon sx={{ fontSize: 32, color: '#6233A0' }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">Manutencao</Typography>
          <Typography variant="body2" color="text.secondary">
            Ativar ou desativar o modo de manutencao nas aplicacoes
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Alert severity="warning" sx={{ mb: 3 }}>
        Ao ativar a manutencao, os utilizadores verao uma pagina informativa em vez do conteudo normal da aplicacao.
      </Alert>

      <Grid container spacing={3}>
        {apps.map((app) => (
          <Grid size={{ xs: 12 }} key={app.id}>
            <MaintenanceCard app={app} onSave={handleSave} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Maintenance;
